import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiError } from '@/auth/AuthContext';
import { Seo } from '@/components/site/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  // Redirect away if already signed in
  useEffect(() => {
    if (user) navigate(user.role === 'admin' ? '/admin' : '/account', { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === 'admin' ? '/admin' : '/account');
    } catch (err) {
      setError(formatApiError(err, 'Sign in failed. Check your email and password.'));
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="min-h-[70vh] flex items-center bg-brand-bone" data-testid="page-login">
      <Seo title="Sign In | HearthOak Construction" description="Sign in to your HearthOak Construction account." path="/login" noindex />
      <div className="container-x max-w-md py-16">
        <div className="eyebrow mb-3">Account</div>
        <h1 className="h2">{forgotMode ? 'Reset your password' : 'Sign in'}</h1>

        {forgotMode ? (
          forgotSent ? (
            <div className="mt-6 border border-brand-border bg-brand-boneAlt p-5" data-testid="forgot-sent">
              <p className="font-sans text-sm text-brand-charcoal">If an account exists for that email, a reset link is on its way. The link is valid for 1 hour.</p>
              <button onClick={() => { setForgotMode(false); setForgotSent(false); }} className="mt-4 font-sans text-sm text-brand-maroon underline underline-offset-4" data-testid="forgot-back">
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={onForgot} className="mt-8 space-y-4">
              <p className="body">Enter your account email and we'll send you a reset link.</p>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="hoc-input" data-testid="forgot-email-input" />
              {error && <p className="font-sans text-sm text-brand-maroon" data-testid="forgot-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="forgot-submit">
                {loading ? 'Sending…' : 'Send reset link'}
              </button>
              <button type="button" onClick={() => setForgotMode(false)} className="w-full text-center font-sans text-sm text-brand-muted hover:text-brand-charcoal" data-testid="forgot-cancel">
                Back to sign in
              </button>
            </form>
          )
        ) : (
          <>
          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/account';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="btn-secondary w-full mt-8 flex items-center justify-center gap-2"
            data-testid="google-login-button"
          >
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-brand-border" />
            <span className="font-sans text-xs uppercase tracking-[0.18em] text-brand-muted">or</span>
            <div className="h-px flex-1 bg-brand-border" />
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="hoc-input" data-testid="login-email-input" autoFocus />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="hoc-input" data-testid="login-password-input" />
            {error && <p className="font-sans text-sm text-brand-maroon" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <div className="flex items-center justify-between font-sans text-sm">
              <button type="button" onClick={() => setForgotMode(true)} className="text-brand-muted hover:text-brand-charcoal" data-testid="forgot-password-link">
                Forgot password?
              </button>
              <Link to="/register" className="text-brand-maroon hover:underline underline-offset-4" data-testid="login-register-link">
                Create an account
              </Link>
            </div>
          </form>
          </>
        )}
      </div>
    </section>
  );
}
